import { useState, useEffect } from 'react'
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd'
import { EyeOutlined, StopOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useMieRichieste, useContestoQuote, useRispondiQuote } from '@/hooks/useWorkshopQuotes'
import type { WorkshopQuote, WorkshopQuoteRispondi } from '@/types/workshopQuotes'

const { Title, Text } = Typography

const STATO_LABEL: Record<string, { label: string; color: string }> = {
  inviata:               { label: 'Da rispondere',         color: 'blue' },
  sopralluogo_richiesto: { label: 'Sopralluogo richiesto', color: 'orange' },
  risposta_ricevuta:     { label: 'Risposta inviata',      color: 'green' },
  approvata:             { label: 'Lavoro affidato',       color: 'green' },
  respinta:              { label: 'Non accettata',         color: 'red' },
  rifiutata_officina:    { label: 'Hai declinato',         color: 'default' },
  annullata:             { label: 'Ritirata dal garage',   color: 'default' },
}

const PRIORITA_COLOR: Record<string, string> = {
  urgente: 'red',
  alta: 'orange',
  media: 'blue',
  bassa: 'default',
}

const TIPO_LAVORO_LABEL: Record<string, string> = {
  riparazione: 'Riparazione',
  sostituzione: 'Sostituzione',
}

/** Miniatura foto con fetch autenticato (chiude M29 — img src diretto bypassa il token) */
function FotoThumb({ thumbnailUrl, id }: { thumbnailUrl: string | null | undefined; id: number }) {
  const { token } = theme.useToken()
  const { src: blobSrc, loading, error } = useAuthenticatedImage(thumbnailUrl)
  return (
    <div
      style={{
        width: 72,
        height: 72,
        borderRadius: token.borderRadius,
        border: `1px solid ${token.colorBorderSecondary}`,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: token.colorFillTertiary,
        flexShrink: 0,
      }}
    >
      {loading && <Spin size="small" />}
      {error && <span style={{ fontSize: 18 }}>⚠️</span>}
      {blobSrc && (
        <img
          src={blobSrc}
          alt={`foto ${id}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
    </div>
  )
}

export default function RichiestePreventivoPage() {
  const { token } = theme.useToken()
  const { setTitle } = usePageTitle()
  useEffect(() => { setTitle('Richieste di preventivo') }, [setTitle])

  const [selectedQuote, setSelectedQuote] = useState<WorkshopQuote | null>(null)
  const [form] = Form.useForm()

  // Modal azioni secondarie (sopralluogo / rifiuto)
  const [secondaryModal, setSecondaryModal] = useState<{
    type: 'sopralluogo' | 'rifiuto'
    note: string
  } | null>(null)
  const [secondaryLoading, setSecondaryLoading] = useState(false)

  const { data: groups = [], isLoading } = useMieRichieste()
  const { data: contesto, isLoading: contLoading } = useContestoQuote(
    selectedQuote?.id ?? 0,
    { enabled: selectedQuote != null },
  )
  const rispondiMutation = useRispondiQuote()

  const handleSelect = (q: WorkshopQuote) => {
    setSelectedQuote(q)
    form.resetFields()
  }

  const handleClose = () => {
    setSelectedQuote(null)
    form.resetFields()
  }

  const handlePreventivo = async (values: any) => {
    if (!selectedQuote) return
    const body: WorkshopQuoteRispondi = {
      esito: 'preventivo',
      importo: Number(values.importo),
      aliquota_iva: Number(values.aliquota_iva),
      giorni_lavorazione: Number(values.giorni_lavorazione),
      note_officina: values.note_officina?.trim() || null,
      ricambi_inclusi: values.ricambi_inclusi === true,
    }
    await rispondiMutation.mutateAsync({ quoteId: selectedQuote.id, body })
    handleClose()
  }

  const handleSecondarySubmit = async () => {
    if (!selectedQuote || !secondaryModal) return
    const note = secondaryModal.note.trim()
    if (!note) return
    setSecondaryLoading(true)
    try {
      await rispondiMutation.mutateAsync({
        quoteId: selectedQuote.id,
        body: { esito: secondaryModal.type, note_officina: note },
      })
      setSecondaryModal(null)
      handleClose()
    } finally {
      setSecondaryLoading(false)
    }
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 800, margin: '0 auto' }}>
      <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Richieste di preventivo
      </Title>

      {isLoading && <Spin />}

      {!isLoading && groups.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nessuna richiesta in attesa"
          style={{ padding: 40 }}
        />
      )}

      {groups.map(({ batch_id, quotes }) => (
        <Card
          key={batch_id ?? `single-${quotes[0]?.id}`}
          size="small"
          style={{ marginBottom: 12 }}
          styles={{ body: { padding: 0 } }}
          title={
            batch_id && quotes.length > 1 ? (
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {quotes.length} interventi
                </Text>
                <Text style={{ fontSize: 11, color: token.colorTextDescription, fontFamily: 'monospace' }}>
                  #{batch_id.slice(0, 8)}
                </Text>
              </Space>
            ) : undefined
          }
        >
          {quotes.map((q, idx) => {
            const s = STATO_LABEL[q.stato] ?? { label: q.stato, color: 'default' }
            return (
              <div
                key={q.id}
                role="button"
                onClick={() => handleSelect(q)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  borderTop: idx > 0 ? `1px solid ${token.colorBorderSecondary}` : undefined,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 12,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = token.colorFillSecondary }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 2 }}>
                    {q.intervento_descrizione || `Intervento #${q.intervention_id}`}
                  </div>
                  <Space size={8} wrap>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(q.data_richiesta).format('DD/MM/YYYY')}
                    </Text>
                    {q.data_scadenza_risposta && (
                      <Text style={{ fontSize: 12, color: token.colorWarning }}>
                        Scade: {dayjs(q.data_scadenza_risposta).format('DD/MM/YYYY')}
                      </Text>
                    )}
                    {q.note_richiesta && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {q.note_richiesta.length > 60
                          ? `${q.note_richiesta.slice(0, 60)}…`
                          : q.note_richiesta}
                      </Text>
                    )}
                  </Space>
                </div>
                <Tag color={s.color} style={{ margin: 0, flexShrink: 0 }}>
                  {s.label}
                </Tag>
              </div>
            )
          })}
        </Card>
      ))}

      {/* ── Drawer dettaglio + form ─────────────────────────────────────────── */}
      <Drawer
        title="Dettaglio richiesta"
        open={selectedQuote != null}
        onClose={handleClose}
        width={520}
        destroyOnClose
      >
        {contLoading || !contesto ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <>
            {/* 1. Danno e lavoro */}
            {(contesto.valutazione_danno || contesto.tipo_lavoro || contesto.priorita || contesto.data_fine_prevista) && (
              <div style={{
                marginBottom: 16,
                padding: '10px 14px',
                background: token.colorFillSecondary,
                borderRadius: token.borderRadius,
              }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Lavoro
                </div>
                <Space wrap size={8}>
                  {contesto.tipo_lavoro && (
                    <Tag>{TIPO_LAVORO_LABEL[contesto.tipo_lavoro] ?? contesto.tipo_lavoro}</Tag>
                  )}
                  {contesto.priorita && (
                    <Tag color={PRIORITA_COLOR[contesto.priorita] ?? 'default'}>
                      {contesto.priorita.charAt(0).toUpperCase() + contesto.priorita.slice(1)}
                    </Tag>
                  )}
                  {contesto.data_fine_prevista && (
                    <Text style={{ fontSize: 12, color: token.colorWarning }}>
                      Consegna prevista: {dayjs(contesto.data_fine_prevista).format('DD/MM/YYYY')}
                    </Text>
                  )}
                </Space>
                {contesto.valutazione_danno && (
                  <div style={{ marginTop: 8, fontSize: 13, color: token.colorText }}>
                    {contesto.valutazione_danno}
                  </div>
                )}
              </div>
            )}

            {/* 2. Veicolo */}
            {(contesto.veicolo_targa || contesto.veicolo_marca) && (
              <div style={{
                marginBottom: 12,
                padding: '8px 12px',
                background: token.colorFillSecondary,
                borderRadius: token.borderRadius,
              }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary, marginBottom: 4 }}>Veicolo</div>
                <Space wrap>
                  {contesto.veicolo_targa && <Tag>{contesto.veicolo_targa}</Tag>}
                  <Text style={{ fontSize: 13 }}>
                    {[contesto.veicolo_marca, contesto.veicolo_modello].filter(Boolean).join(' ')}
                    {contesto.veicolo_anno ? ` (${contesto.veicolo_anno})` : ''}
                  </Text>
                </Space>
              </div>
            )}

            {/* 3. Intervento richiesto */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
                {contesto.descrizione_intervento}
              </div>
              <Space wrap>
                {contesto.numero_scheda && <Tag>Scheda {contesto.numero_scheda}</Tag>}
                <Tag>{contesto.tipo_intervento}</Tag>
                {contesto.durata_stimata > 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {contesto.durata_stimata}h stimate
                  </Text>
                )}
              </Space>
              {contesto.note_intervento && (
                <div style={{ marginTop: 8, fontSize: 13, color: token.colorTextSecondary }}>
                  {contesto.note_intervento}
                </div>
              )}
            </div>

            {/* 4. Ricambi previsti */}
            {contesto.ha_ricambi ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Ricambi previsti
                </div>
                <Table
                  size="small"
                  dataSource={contesto.ricambi}
                  rowKey={(r, i) => `${r.descrizione}-${i}`}
                  pagination={false}
                  columns={[
                    { title: 'Descrizione', dataIndex: 'descrizione', key: 'desc', ellipsis: true },
                    { title: 'Qtà', dataIndex: 'quantita', key: 'qty', width: 60, align: 'right' },
                    { title: 'U.m.', dataIndex: 'unita_misura', key: 'um', width: 50 },
                  ]}
                />
              </div>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Nessun ricambio ancora definito"
                style={{ marginBottom: 12 }}
              />
            )}

            {/* 5. Foto */}
            {contesto.ha_foto && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Foto
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {contesto.foto.map(f => (
                    <FotoThumb key={f.id} thumbnailUrl={f.thumbnail_url} id={f.id} />
                  ))}
                </div>
              </div>
            )}

            {/* 7. Note del richiedente e scadenza */}
            {contesto.note_richiesta && (
              <Alert
                type="info"
                showIcon
                message="Note del richiedente"
                description={contesto.note_richiesta}
                style={{ marginBottom: 12 }}
              />
            )}
            {contesto.data_scadenza_risposta && (
              <Alert
                type="warning"
                showIcon
                message={`Risposta richiesta entro: ${dayjs(contesto.data_scadenza_risposta).format('DD/MM/YYYY')}`}
                style={{ marginBottom: 12 }}
              />
            )}

            <Divider />

            {/* Avviso rinegoziazione (tutti gli stati) */}
            {selectedQuote && selectedQuote.revisione > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`Richiesta rivista dal garage (revisione ${selectedQuote.revisione})`}
                description={(() => {
                  const imp = contesto.importo_precedente
                  let offertaRiga: string | null = null
                  if (imp != null) {
                    const impFmt = imp.toFixed(2).replace('.', ',')
                    const parti: string[] = [`€ ${impFmt}`]
                    if (contesto.aliquota_precedente != null) {
                      parti.push(`+ IVA ${contesto.aliquota_precedente}%`)
                    }
                    if (contesto.giorni_precedenti != null) {
                      parti.push(`· ${contesto.giorni_precedenti} giorni`)
                    }
                    parti.push(contesto.ricambi_inclusi_precedenti ? '· ricambi inclusi' : '· solo manodopera')
                    offertaRiga = `Tua offerta precedente: ${parti.join(' ')}`
                  }
                  const noteRiga = contesto.note_precedenti?.trim() || null
                  return (
                    <div>
                      {selectedQuote.motivo_rifiuto && <div>{selectedQuote.motivo_rifiuto}</div>}
                      {offertaRiga && <div style={{ marginTop: selectedQuote.motivo_rifiuto ? 6 : 0 }}>{offertaRiga}</div>}
                      {noteRiga && <div>Note: {noteRiga}</div>}
                    </div>
                  )
                })()}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Form risposta (inviata / sopralluogo_richiesto) o riepilogo stato */}
            {selectedQuote && (selectedQuote.stato === 'inviata' || selectedQuote.stato === 'sopralluogo_richiesto') ? (
              <>
                {selectedQuote.stato === 'sopralluogo_richiesto' && (
                  <Alert
                    type="info"
                    showIcon
                    message="Hai richiesto un sopralluogo. Dopo aver visto il veicolo puoi inviare il preventivo qui sotto."
                    description={selectedQuote.note_officina
                      ? `Note inviate: "${selectedQuote.note_officina}"${selectedQuote.data_risposta ? ` · ${dayjs(selectedQuote.data_risposta).format('DD/MM/YYYY')}` : ''}`
                      : undefined}
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Form form={form} layout="vertical" onFinish={handlePreventivo}>
                  <Alert
                    type="info"
                    showIcon
                    message="I ricambi previsti sono forniti dal garage. Quota la sola manodopera, salvo diversa indicazione."
                    style={{ marginBottom: 16 }}
                  />

                  <Form.Item name="ricambi_inclusi" valuePropName="checked" initialValue={false} style={{ marginBottom: 16 }}>
                    <Checkbox>Il preventivo include i ricambi</Checkbox>
                  </Form.Item>

                  <Form.Item
                    name="importo"
                    label="Importo imponibile"
                    rules={[
                      { required: true, message: "Inserisci l'importo" },
                      { type: 'number', min: 0.01, message: "L'importo deve essere maggiore di zero" },
                      { type: 'number', max: 99999999.99, message: "L'importo non può superare 99.999.999,99 €" },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0.01}
                      max={99999999.99}
                      step={0.01}
                      precision={2}
                      placeholder="Es. 350.00"
                      addonAfter="€"
                    />
                  </Form.Item>

                  <Form.Item
                    name="aliquota_iva"
                    label="Aliquota IVA"
                    rules={[
                      { required: true, message: "Inserisci l'aliquota IVA" },
                      { type: 'number', min: 0, max: 100, message: 'Valore tra 0 e 100' },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      step={1}
                      precision={2}
                      placeholder="Es. 22"
                      addonAfter="%"
                    />
                  </Form.Item>

                  <Form.Item
                    name="giorni_lavorazione"
                    label="Giorni lavorativi stimati"
                    rules={[
                      { required: true, message: 'Inserisci i giorni stimati' },
                      { type: 'number', min: 1, message: 'Inserisci almeno 1 giorno' },
                      { type: 'number', max: 365, message: 'Non può superare 365 giorni' },
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={365}
                      step={1}
                      precision={0}
                      placeholder="Es. 3"
                    />
                  </Form.Item>

                  <Form.Item name="note_officina" label="Note aggiuntive">
                    <Input.TextArea rows={2} placeholder="Condizioni, esclusioni, note…" />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 8 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={rispondiMutation.isPending}
                      block
                      size="large"
                    >
                      Invia preventivo
                    </Button>
                  </Form.Item>
                </Form>

                {/* Azioni secondarie */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    style={{ color: token.colorTextSecondary }}
                    onClick={() => setSecondaryModal({ type: 'sopralluogo', note: '' })}
                  >
                    Devo vedere il veicolo
                  </Button>
                  <Button
                    type="text"
                    icon={<StopOutlined />}
                    size="small"
                    danger
                    onClick={() => setSecondaryModal({ type: 'rifiuto', note: '' })}
                  >
                    Non posso occuparmene
                  </Button>
                </div>
              </>
            ) : selectedQuote?.stato === 'risposta_ricevuta' ? (
              <Alert
                type="success"
                showIcon
                message="Preventivo inviato, in attesa del garage"
                description={
                  <div style={{ marginTop: 6 }}>
                    <div>
                      {`€ ${Number(selectedQuote.importo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
                      {selectedQuote.aliquota_iva != null && ` + IVA ${selectedQuote.aliquota_iva}%`}
                      {selectedQuote.giorni_lavorazione != null && ` · ${selectedQuote.giorni_lavorazione} gg`}
                    </div>
                    <div style={{ marginTop: 2 }}>
                      {selectedQuote.ricambi_inclusi ? 'Ricambi inclusi' : 'Solo manodopera'}
                    </div>
                    {selectedQuote.note_officina && (
                      <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                        &ldquo;{selectedQuote.note_officina}&rdquo;
                      </div>
                    )}
                    {selectedQuote.data_risposta && (
                      <div style={{ marginTop: 4, opacity: 0.7, fontSize: 12 }}>
                        Inviato il {dayjs(selectedQuote.data_risposta).format('DD/MM/YYYY')}
                      </div>
                    )}
                  </div>
                }
              />
            ) : selectedQuote?.stato === 'approvata' ? (
              <Alert
                type="success"
                showIcon
                message="Lavoro affidato a te"
                description={
                  selectedQuote.importo != null ? (
                    <div style={{ marginTop: 4 }}>
                      {`€ ${Number(selectedQuote.importo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
                      {selectedQuote.aliquota_iva != null && ` + IVA ${selectedQuote.aliquota_iva}%`}
                      {selectedQuote.giorni_lavorazione != null && ` · ${selectedQuote.giorni_lavorazione} gg`}
                    </div>
                  ) : undefined
                }
              />
            ) : selectedQuote?.stato === 'respinta' ? (
              <Alert
                type="error"
                showIcon
                message="Il garage non ha accettato il preventivo"
                description={selectedQuote.motivo_rifiuto || undefined}
              />
            ) : selectedQuote?.stato === 'rifiutata_officina' ? (
              <Alert
                type="warning"
                showIcon
                message="Hai declinato la richiesta"
                description={
                  <>
                    {selectedQuote.note_officina && <div>{selectedQuote.note_officina}</div>}
                    {selectedQuote.data_risposta && (
                      <div style={{ marginTop: 4, opacity: 0.7, fontSize: 12 }}>
                        {dayjs(selectedQuote.data_risposta).format('DD/MM/YYYY')}
                      </div>
                    )}
                  </>
                }
              />
            ) : selectedQuote?.stato === 'annullata' ? (
              <Alert
                type="warning"
                showIcon
                message="Il garage ha ritirato la richiesta"
              />
            ) : null}
          </>
        )}
      </Drawer>

      {/* ── Modal azioni secondarie ─────────────────────────────────────────── */}
      <Modal
        open={secondaryModal != null}
        title={
          secondaryModal?.type === 'sopralluogo'
            ? 'Richiesta di sopralluogo'
            : 'Impossibilità a eseguire il lavoro'
        }
        onCancel={() => setSecondaryModal(null)}
        onOk={handleSecondarySubmit}
        okText={secondaryModal?.type === 'sopralluogo' ? 'Invia avviso' : 'Conferma'}
        okButtonProps={{
          danger: secondaryModal?.type === 'rifiuto',
          loading: secondaryLoading,
          disabled: !secondaryModal?.note.trim(),
        }}
        cancelText="Annulla"
        destroyOnClose
      >
        <p style={{ color: token.colorTextSecondary, marginBottom: 12 }}>
          {secondaryModal?.type === 'sopralluogo'
            ? 'Indica perché hai bisogno di vedere il veicolo. La richiesta resterà aperta: potrai rispondere dopo il sopralluogo.'
            : 'Indica il motivo per cui non puoi eseguire questo lavoro.'}
        </p>
        <Input.TextArea
          rows={3}
          placeholder={
            secondaryModal?.type === 'sopralluogo'
              ? 'Es. Necessario valutare l\'entità del danno di persona…'
              : 'Es. Non disponiamo dell\'attrezzatura necessaria…'
          }
          value={secondaryModal?.note ?? ''}
          onChange={e => setSecondaryModal(prev => prev ? { ...prev, note: e.target.value } : null)}
        />
      </Modal>
    </div>
  )
}
